import { AsyncSubject, Observable } from 'rxjs';
import { Auth } from '@aws-amplify/auth';
import { AuthOptions } from '@aws-amplify/auth/lib-esm/types';
import { usePortalName } from '../hooks/usePortalName';

const { __environment__ } = window as any;

const region = __environment__.REACT_APP_AWS_USER_POOL_REGION;
const userPool = __environment__.REACT_APP_AWS_USER_POOL_ID;
const clientId = __environment__.REACT_APP_AWS_CLIENT_ID;
const domain = __environment__.REACT_APP_AWS_DOMAIN;
const signinUri = __environment__.REACT_APP_AWS_SIGNIN_URI;
const signoutUri = __environment__.REACT_APP_AWS_SIGNOUT_URI;

export interface Tokens {
  accessToken?: string;
  idToken?: string;
  accessTokenExpirationMs?: number;
}

class AuthService {
  private loggedIn = false;
  private inFlightTokensSubject: AsyncSubject<Tokens> | null = null;

  constructor() {
    const portalName = usePortalName();
    if (portalName) {
      const config = this.getPortalConfiguration(portalName);
      Auth.configure(config);
    } else {
      Auth.configure({
        region: region,
        userPoolId: userPool,
        userPoolWebClientId: clientId,
        oauth: {
          domain: domain,
          scope: [
            'email',
            'profile',
            'openid',
            'aws.cognito.signin.user.admin',
          ],
          redirectSignIn: signinUri,
          redirectSignOut: signoutUri,
          responseType: 'code',
        },
      });
    }

    Auth.currentSession().then((session) => {
      this.loggedIn = session.isValid();
    });
  }

  private getPortalRegion(portalName: string): string {
    const envName = `REACT_APP_AWS_USER_POOL_REGION_${portalName.toUpperCase()}`;
    const envVal = __environment__[envName];
    return envVal
      ? envVal
      : __environment__.REACT_APP_AWS_USER_POOL_REGION_EXTERNAL;
  }

  private getPortalUserPool(portalName: string): string {
    const envName = `REACT_APP_AWS_USER_POOL_ID_${portalName.toUpperCase()}`;
    const envVal = __environment__[envName];
    return envVal
      ? envVal
      : __environment__.REACT_APP_AWS_USER_POOL_ID_EXTERNAL;
  }

  private getPortalClientID(portalName: string): string {
    const envName = `REACT_APP_AWS_CLIENT_ID_${portalName.toUpperCase()}`;
    const envVal = __environment__[envName];
    return envVal ? envVal : __environment__.REACT_APP_AWS_CLIENT_ID_EXTERNAL;
  }

  private getPortalDomain(portalName: string): string {
    const envName = `REACT_APP_AWS_DOMAIN_${portalName.toUpperCase()}`;
    const envVal = __environment__[envName];
    return envVal ? envVal : __environment__.REACT_APP_AWS_DOMAIN_EXTERNAL;
  }

  private getPortalConfiguration(portalName: string): AuthOptions {
    return {
      region: this.getPortalRegion(portalName),
      userPoolId: this.getPortalUserPool(portalName),
      userPoolWebClientId: this.getPortalClientID(portalName),
      oauth: {
        domain: this.getPortalDomain(portalName),
        scope: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
        redirectSignIn: this.getExternalSigninUrl(portalName),
        redirectSignOut: this.getExternalSignoutUrl(portalName),
        responseType: 'code',
      },
    };
  }

  private getExternalUrl(name: string) {
    return __environment__[name];
  }

  private getExternalSigninUrl(portalName: string) {
    return this.getExternalUrl(
      `REACT_APP_AWS_SIGNIN_URI_EXTERNAL_${portalName.toUpperCase()}`,
    );
  }

  private getExternalSignoutUrl(portalName: string) {
    return this.getExternalUrl(
      `REACT_APP_AWS_SIGNOUT_URI_EXTERNAL_${portalName.toUpperCase()}`,
    );
  }

  private checkSession() {
    Auth.currentSession()
      .then((session) => {
        this.loggedIn = session.isValid();
      })
      .catch((_err) => {
        this.loggedIn = false;
      });
  }

  public getFederatedCustomProvider(portalName: string | null): string | null {
    if (portalName) {
      const envName = `REACT_APP_AWS_CUSTOM_PROVIDER_${portalName.toUpperCase()}`;
      const envVal = __environment__[envName];
      return envVal ? envVal : null;
    }
    return __environment__.REACT_APP_AWS_CUSTOM_PROVIDER;
  }

  public getUserInfo(): Observable<{
    name: string;
    email: string;
  }> {
    const subject = new AsyncSubject<{
      name: string;
      email: string;
    }>();
    Auth.currentAuthenticatedUser().then((userInfo) => {
      subject.next({
        name: `${userInfo.attributes.given_name} ${userInfo.attributes.family_name}`,
        email: userInfo.attributes.email,
      });
      subject.complete();
    });
    return subject;
  }

  public isLoggedIn(): boolean {
    return this.loggedIn;
  }

  public getTokens(): Observable<Tokens> {
    if (this.inFlightTokensSubject) {
      return this.inFlightTokensSubject;
    }

    const subject = new AsyncSubject<Tokens>();
    this.inFlightTokensSubject = subject;

    Auth.currentSession()
      .then((session) => {
        const accessToken = session?.getAccessToken();
        const tokenData: Tokens = {
          accessToken: accessToken?.getJwtToken(),
          idToken: session?.getIdToken()?.getJwtToken(),
        };

        const accessTokenExpirationSeconds = accessToken?.getExpiration?.();
        if (typeof accessTokenExpirationSeconds === 'number') {
          tokenData.accessTokenExpirationMs =
            accessTokenExpirationSeconds * 1000;
        }

        subject.next({
          ...tokenData,
        });
        subject.complete();
      })
      .catch(() => {
        subject.next({});
        subject.complete();
      })
      .finally(() => {
        this.inFlightTokensSubject = null;
      });

    return subject;
  }

  public memberLogin(
    username: string,
    password: string,
    isProvider: boolean = true,
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      Auth.configure({
        authenticationFlowType: isProvider
          ? 'USER_PASSWORD_AUTH'
          : 'USER_SRP_AUTH',
      });

      Auth.signIn(username, password)
        .then((value) => {
          if (value.challengeName === 'NEW_PASSWORD_REQUIRED') {
            resolve(value);
          } else {
            this.loggedIn = true;
            resolve(value);
          }
        })
        .catch((err) => {
          reject(err?.message || 'Login failed');
        });
    });
  }

  public async handleLoginCallback(_url: string): Promise<void> {
    return new Promise(async (resolve) => {
      Auth.currentSession()
        .then((session) => {
          this.loggedIn = session.isValid();
          // This is required for the portal so it can force
          // the application router to be initiated again.
          window.location.href = '/';
        })
        .finally(() => resolve());
    });
  }

  public loginFederated(customProvider: string): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.federatedSignIn({
        customProvider,
      })
        .then((value) => {
          this.checkSession();
          resolve();
        })
        .catch((err) => {
          reject('Login failed');
        });
    });
  }

  public logout(): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.signOut()
        .then(() => {
          this.checkSession();
          resolve();
        })
        .catch((err) => {
          reject('Logout failed');
        });
    });
  }
}

const authService = new AuthService();

export default authService;
